/** Builder → When it runs: plain choices over the agent's triggers (ADR-0062). */
export default {
  en: {
    tab: { triggers: "When it runs" },
    cancel: "Cancel",
    title: "When should this agent run?",
    intro: "Choose what starts the agent. You can change this at any time; it takes effect when you release the version.",
    loadFailed: "The agent could not be loaded",
    env: { dev: "development", test: "testing", staging: "final check", prod: "Live" },
    kind: {
      manual: { label: "Only when someone asks", help: "People start it from the Assistant or a channel. This is the usual choice." },
      schedule: { label: "On a schedule", help: "Every day, every working day, every week, every month or every few hours." },
      webhook: { label: "When another system calls it", help: "Another system sends it a message over the network with a secret key." },
      email: { label: "When a new e-mail arrives", help: "It reads each new message in a mailbox." },
      file_drop: { label: "When a new file arrives", help: "It handles each new file put in a folder." },
      queue: { label: "When a message arrives in a queue", help: "It handles each new message from a business system's queue." },
    },
    system: "Connected system",
    systemHelp: "Pick the connection set up by your administrators.",
    systemPlaceholder: "Choose a system",
    email: { title: "Which mailbox", mailbox: "Mailbox address", mailboxHelp: "The address whose new messages start the agent.", folder: "Folder", folderHelp: "Usually the inbox." },
    file: { title: "Which folder", location: "Folder", locationHelp: "Where new files arrive.", pattern: "Which files", patternHelp: "For example *.pdf for PDF files only; * for every file." },
    queue: { title: "Which queue", topic: "Queue name", topicHelp: "Ask the system's owner for the exact name." },
    alsoManual: "People can also start it by asking",
    soon: {
      tag: "Coming soon — not available yet",
      used: "This way of starting never starts the agent yet",
      usedText: "No part of the platform listens for new files or queue messages yet, so the agent would never run. Choose another way to start it, such as a schedule or another system calling it.",
    },
    autonomy: {
      guided: "Agents made in the guided builder may work on their own only to run on their schedule or event. If it changes anything, a person checks the change first (step 3), and Testing and Live still need a release approver's sign-off.",
      on: "This agent may work on its own",
      needed: "Allow this agent to work on its own",
      why: "When a schedule or another system starts the agent, no person is asking. The platform only allows that for agents that are allowed to work on their own.",
      switch: "Allow it to work on its own",
      blocked: {
        nocode: "Choose a schedule or an event first: an agent from the guided builder works on its own only when one starts it.",
        tier: "High-risk agents (risk tier 3 or 4) always work for a person and cannot run on their own.",
      },
    },
    schedule: {
      title: "Schedule",
      how: "How often",
      frequency: { daily: "Every day", weekdays: "Every working day", weekly: "Every week", monthly: "Every month", hourly: "Every few hours" },
      day: "On",
      weekday: { "0": "Sunday", "1": "Monday", "2": "Tuesday", "3": "Wednesday", "4": "Thursday", "5": "Friday", "6": "Saturday" },
      workWeek: "Working week",
      workWeekOption: { "sun-thu": "Sunday to Thursday", "mon-fri": "Monday to Friday" },
      date: "On day",
      dateHelp: "Days 1 to 28, so it runs every month.",
      every: "Every",
      hours_one: "{{count}} hour",
      hours_other: "{{count}} hours",
      time: "At",
      zone: "Time zone",
      zoneDefault: "Your organisation's time zone.",
      zoneOverride: "Different from your organisation's time zone.",
      zoneTenant: "{{zone}} (your organisation)",
      next: "Next 3 runs:",
      custom: "This agent has a custom schedule",
      customText: "A developer set a schedule this screen cannot show. It stays as it is unless you replace it with a simple schedule.",
      replace: "Replace with a simple schedule",
    },
    webhook: {
      testEvent: "The test event",
      testEventSample: "This is the sample event you gave. Change it if you like, then send it.",
      testEventNoSample: "There is no sample event yet, so this is a simple one. Add a sample event above to test with your own fields.",
      testEventInvalid: "This isn't a valid event: it must be a JSON object such as {\"order_id\": \"SO-1\"}.",
      testSend: "Send this event",
      title: "Connection for other systems",
      intro: "Give the other system this address and the secret key. Each call must be signed with the key; calls without a valid signature are refused.",
      pickAgent: "Choose an agent first.",
      address: "Address:",
      secret: "Secret key:",
      configured: "A secret key was created on {{at}} by {{by}}.",
      notConfigured: "No secret key yet. Create one to let other systems call this agent.",
      create: "Create the secret key",
      createFailed: "The secret key could not be created: {{message}}",
      rotate: "Make a new secret key",
      rotateTitle: "Make a new secret key?",
      rotateText: "The old key stops working at once. Systems that use it must be given the new key.",
      rotateOk: "Make a new key",
      onceTitle: "Copy the secret key now",
      onceWarning: "This is the only time the key is shown. Store it in the other system now; you can always make a new one.",
      onceDone: "I have copied it",
      howToSign: "The other system signs the body of each call with this key (HMAC-SHA256) and sends the result in the {{header}} header.",
      test: "Send a test event",
      samplePayload: "Test event from the agent builder",
      signatureOk: "The test event arrived and its signature was accepted.",
      signatureBad: "The signature was not accepted. Make a new secret key and try again.",
      testRun: "What the agent did with the test event",
      testRunSafe: "Safe test: look-ups ran for real; actions that would change something were simulated, nothing was changed.",
      problem: {
        no_secret: "Create the secret key first.",
        not_released: "Release the agent to development first, then send a test event.",
        not_dev: "Test events are only available in development.",
        no_identity: "This agent has no identity yet. Release it to development again.",
      },
    },
    status: {
      title: "Where it runs now",
      runs: "Version {{version}} runs in {{env}}.",
      notReleased: "This agent is not released anywhere yet.",
      scheduleLive: "The schedule is active in development — next run {{when}}.",
      scheduleStarts: "The schedule starts once you release this version to development.",
      otherVersion: "You are editing {{version}}; development runs {{dev}}.",
    },
    issue: {
      title: "A few details are missing",
      mailbox: "Enter the mailbox address.",
      location: "Enter the folder.",
      topic: "Enter the queue name.",
      schedule: "Choose a valid time.",
    },
    save: {
      asVersion: "Save as version {{version}}",
      releaseVersion: "Release version {{version}} to Development",
      inDev: "Version {{version}} runs in Development",
      button: "Save as draft",
      release: "Release to development",
      releaseReason: "Released from the builder to start its schedule or connection",
      saved: "Saved draft {{version}}",
      newDraft: "Saved as draft {{version}} — release it to start the schedule",
      savedAs: "Saved as version {{version}}",
      startsAfterRelease: "Schedules and connections start once this version is released. Release it to development to try it now.",
      released: "Version {{version}} now runs in development",
      releaseWaiting: "Version {{version}} is waiting to be released",
      failed: "Saving did not work: {{message}}",
      submittedNote: "Version {{version}} is saved and can't change, so your changes become version {{next}}.",
      draftNote: "Your changes are saved to this version, ready to release.",
      readOnly: "Only builders can change when an agent runs.",
    },
  },
  ar: {
    tab: { triggers: "متى يعمل" },
    cancel: "إلغاء",
    title: "متى يجب أن يعمل هذا الوكيل؟",
    intro: "اختر ما يبدأ عمل الوكيل. يمكنك تغيير ذلك في أي وقت، ويسري عند إطلاق الإصدار.",
    loadFailed: "تعذّر تحميل الوكيل",
    env: { dev: "التطوير", test: "الاختبار", staging: "الفحص النهائي", prod: "التشغيل الفعلي" },
    kind: {
      manual: { label: "عندما يطلب شخص فقط", help: "يبدؤه الناس من المساعد أو من قناة. هذا هو الخيار المعتاد." },
      schedule: { label: "وفق جدول زمني", help: "كل يوم، أو كل يوم عمل، أو كل أسبوع، أو كل شهر، أو كل بضع ساعات." },
      webhook: { label: "عندما يستدعيه نظام آخر", help: "يرسل نظام آخر رسالة عبر الشبكة مع مفتاح سري." },
      email: { label: "عند وصول بريد إلكتروني جديد", help: "يقرأ كل رسالة جديدة في صندوق بريد." },
      file_drop: { label: "عند وصول ملف جديد", help: "يعالج كل ملف جديد يوضع في مجلد." },
      queue: { label: "عند وصول رسالة إلى طابور", help: "يعالج كل رسالة جديدة من طابور نظام أعمال." },
    },
    system: "النظام المتصل",
    systemHelp: "اختر الاتصال الذي أعدّه المسؤولون.",
    systemPlaceholder: "اختر نظامًا",
    email: { title: "أي صندوق بريد", mailbox: "عنوان صندوق البريد", mailboxHelp: "العنوان الذي تبدأ رسائله الجديدة عمل الوكيل.", folder: "المجلد", folderHelp: "عادةً صندوق الوارد." },
    file: { title: "أي مجلد", location: "المجلد", locationHelp: "المكان الذي تصل إليه الملفات الجديدة.", pattern: "أي ملفات", patternHelp: "مثلًا ‎*.pdf‎ لملفات PDF فقط، و‎*‎ لكل الملفات." },
    queue: { title: "أي طابور", topic: "اسم الطابور", topicHelp: "اطلب الاسم الدقيق من مالك النظام." },
    alsoManual: "يمكن للناس أيضًا تشغيله بطلب منهم",
    soon: {
      tag: "قريبًا — غير متاح بعد",
      used: "طريقة البدء هذه لا تشغّل الوكيل بعد",
      usedText: "لا يوجد في المنصة بعد ما يستقبل الملفات الجديدة أو رسائل قوائم الانتظار، لذلك لن يعمل الوكيل أبدًا. اختر طريقة أخرى لبدئه، مثل جدول زمني أو استدعاء من نظام آخر.",
    },
    autonomy: {
      guided: "يمكن للوكلاء المصنوعين في المنشئ الموجّه العمل وحدهم فقط ليعملوا وفق جدولهم أو حدثهم. وإن غيّر شيئًا، يراجع شخص التغيير أولًا (الخطوة 3)، وتبقى مرحلتا الاختبار والتشغيل الفعلي بحاجة إلى موافقة معتمِد الإطلاق.",
      on: "يُسمح لهذا الوكيل بالعمل وحده",
      needed: "اسمح لهذا الوكيل بالعمل وحده",
      why: "عندما يبدأ جدول زمني أو نظام آخر عمل الوكيل، لا يوجد شخص يطلب. لا تسمح المنصة بذلك إلا للوكلاء المسموح لهم بالعمل وحدهم.",
      switch: "اسمح له بالعمل وحده",
      blocked: {
        nocode: "اختر جدولًا زمنيًا أو حدثًا أولًا: يعمل وكيل المنشئ الموجّه وحده فقط عندما يبدأه أحدهما.",
        tier: "الوكلاء عالو المخاطر (مستوى المخاطر 3 أو 4) يعملون دائمًا لصالح شخص ولا يمكنهم العمل وحدهم.",
      },
    },
    schedule: {
      title: "الجدول الزمني",
      how: "كم مرة",
      frequency: { daily: "كل يوم", weekdays: "كل يوم عمل", weekly: "كل أسبوع", monthly: "كل شهر", hourly: "كل بضع ساعات" },
      day: "يوم",
      weekday: { "0": "الأحد", "1": "الاثنين", "2": "الثلاثاء", "3": "الأربعاء", "4": "الخميس", "5": "الجمعة", "6": "السبت" },
      workWeek: "أسبوع العمل",
      workWeekOption: { "sun-thu": "من الأحد إلى الخميس", "mon-fri": "من الاثنين إلى الجمعة" },
      date: "في اليوم",
      dateHelp: "من اليوم 1 إلى 28، ليعمل في كل شهر.",
      every: "كل",
      hours_zero: "{{count}} ساعة",
      hours_one: "ساعة واحدة",
      hours_two: "ساعتين",
      hours_few: "{{count}} ساعات",
      hours_many: "{{count}} ساعة",
      hours_other: "{{count}} ساعة",
      time: "في الساعة",
      zone: "المنطقة الزمنية",
      zoneDefault: "المنطقة الزمنية لمؤسستك.",
      zoneOverride: "تختلف عن المنطقة الزمنية لمؤسستك.",
      zoneTenant: "{{zone}} (مؤسستك)",
      next: "التشغيلات الثلاثة القادمة:",
      custom: "لهذا الوكيل جدول زمني مخصص",
      customText: "وضع مطوّر جدولًا لا تستطيع هذه الشاشة عرضه. يبقى كما هو ما لم تستبدله بجدول بسيط.",
      replace: "استبدله بجدول بسيط",
    },
    webhook: {
      testEvent: "حدث الاختبار",
      testEventSample: "هذا هو نموذج الحدث الذي أدخلته. غيّره إن شئت ثم أرسله.",
      testEventNoSample: "لا يوجد نموذج حدث بعد، لذا هذا حدث بسيط. أضف نموذج حدث في الأعلى لتختبر بحقولك.",
      testEventInvalid: "هذا ليس حدثًا صالحًا: يجب أن يكون كائن JSON مثل {\"order_id\": \"SO-1\"}.",
      testSend: "أرسل هذا الحدث",
      title: "الاتصال للأنظمة الأخرى",
      intro: "أعطِ النظام الآخر هذا العنوان والمفتاح السري. يجب توقيع كل استدعاء بالمفتاح؛ وتُرفض الاستدعاءات التي لا تحمل توقيعًا صحيحًا.",
      pickAgent: "اختر وكيلًا أولًا.",
      address: "العنوان:",
      secret: "المفتاح السري:",
      configured: "أُنشئ مفتاح سري في {{at}} بواسطة {{by}}.",
      notConfigured: "لا يوجد مفتاح سري بعد. أنشئ واحدًا لتسمح للأنظمة الأخرى باستدعاء هذا الوكيل.",
      create: "أنشئ المفتاح السري",
      createFailed: "تعذّر إنشاء المفتاح السري: {{message}}",
      rotate: "أنشئ مفتاحًا سريًا جديدًا",
      rotateTitle: "هل تنشئ مفتاحًا سريًا جديدًا؟",
      rotateText: "يتوقف المفتاح القديم عن العمل فورًا. يجب إعطاء المفتاح الجديد للأنظمة التي تستخدمه.",
      rotateOk: "أنشئ مفتاحًا جديدًا",
      onceTitle: "انسخ المفتاح السري الآن",
      onceWarning: "هذه هي المرة الوحيدة التي يُعرض فيها المفتاح. احفظه في النظام الآخر الآن؛ ويمكنك دائمًا إنشاء مفتاح جديد.",
      onceDone: "نسخته",
      howToSign: "يوقّع النظام الآخر محتوى كل استدعاء بهذا المفتاح (HMAC-SHA256) ويرسل النتيجة في الترويسة {{header}}.",
      test: "أرسل حدثًا تجريبيًا",
      samplePayload: "حدث تجريبي من منشئ الوكلاء",
      signatureOk: "وصل الحدث التجريبي وقُبل توقيعه.",
      signatureBad: "لم يُقبل التوقيع. أنشئ مفتاحًا سريًا جديدًا وحاول مرة أخرى.",
      testRun: "ما فعله الوكيل بالحدث التجريبي",
      testRunSafe: "اختبار آمن: نُفّذت عمليات الاستعلام فعليًا، وكانت الإجراءات التي تغيّر شيئًا محاكاة، ولم يتغير شيء.",
      problem: {
        no_secret: "أنشئ المفتاح السري أولًا.",
        not_released: "أطلق الوكيل إلى مرحلة التطوير أولًا، ثم أرسل حدثًا تجريبيًا.",
        not_dev: "الأحداث التجريبية متاحة في مرحلة التطوير فقط.",
        no_identity: "ليست لهذا الوكيل هوية بعد. أطلقه إلى مرحلة التطوير مرة أخرى.",
      },
    },
    status: {
      title: "أين يعمل الآن",
      runs: "يعمل الإصدار {{version}} في {{env}}.",
      notReleased: "لم يُطلق هذا الوكيل في أي مكان بعد.",
      scheduleLive: "الجدول الزمني فعّال في مرحلة التطوير — التشغيل القادم {{when}}.",
      scheduleStarts: "يبدأ الجدول الزمني عند إطلاق هذا الإصدار إلى مرحلة التطوير.",
      otherVersion: "أنت تعدّل {{version}}؛ ومرحلة التطوير تشغّل {{dev}}.",
    },
    issue: {
      title: "بعض التفاصيل ناقصة",
      mailbox: "أدخل عنوان صندوق البريد.",
      location: "أدخل المجلد.",
      topic: "أدخل اسم الطابور.",
      schedule: "اختر وقتًا صحيحًا.",
    },
    save: {
      asVersion: "احفظه كالإصدار {{version}}",
      releaseVersion: "أطلق الإصدار {{version}} إلى مرحلة التطوير",
      inDev: "الإصدار {{version}} يعمل في مرحلة التطوير",
      button: "احفظ كمسودة",
      release: "أطلقه إلى مرحلة التطوير",
      releaseReason: "أُطلق من منشئ الوكلاء لبدء جدوله الزمني أو اتصاله",
      saved: "حُفظت المسودة {{version}}",
      newDraft: "حُفظ كمسودة {{version}} — أطلقها لبدء الجدول الزمني",
      savedAs: "حُفظ كالإصدار {{version}}",
      startsAfterRelease: "تبدأ الجداول الزمنية والاتصالات عند إطلاق هذا الإصدار. أطلقه إلى مرحلة التطوير لتجربته الآن.",
      released: "يعمل الإصدار {{version}} الآن في مرحلة التطوير",
      releaseWaiting: "ينتظر الإصدار {{version}} إطلاقه",
      failed: "لم ينجح الحفظ: {{message}}",
      submittedNote: "الإصدار {{version}} محفوظ ولا يمكن تغييره، لذلك تصبح تغييراتك الإصدار {{next}}.",
      draftNote: "تُحفظ تغييراتك في هذا الإصدار، جاهزة للإطلاق.",
      readOnly: "يستطيع منشئو الوكلاء فقط تغيير متى يعمل الوكيل.",
    },
  },
};
